import {
	Checkbox,
	ComboBox,
	DeprecatedCreativeModal as CreativeModal,
	DeprecatedScrollbar as Scrollbar,
	FlexColumn,
	FlexRow,
	SearchField,
	SubHeading,
	Text,
} from '@forecast-it/design-system';
import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {createFragmentContainer, graphql} from 'react-relay';
import styled from 'styled-components/macro';
import {CheckedState} from '@radix-ui/react-checkbox';
import {hideLoader, showLoader} from '../../global_loader';
import DirectApi from '../../../directApi';
import {
	ExternalRef,
	PersonInputType,
} from '../../../mutations/integrations/azure_devops/__generated__/CreateAdoUsersMutation.graphql';
import CreateAdoUsersMutation from '../../../mutations/integrations/azure_devops/CreateAdoUsersMutation';
import {CommitMutation} from '../../../mutations/CommitMutation';
import {getSelectableProfiles, personHasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import type {AdoImportUsersModal_company$data as AdoImportUsersModal_company} from './__generated__/AdoImportUsersModal_company.graphql';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import {ConnectionEdge} from '@/forecast-app/shared/util/type-util/Connection';
import {type Profile} from '@/containers/settings/types';

const ListItem = styled.div`
	width: 550px;
	padding: 18px 8px 18px 8px;
	border-bottom: 1px solid #ccc;
`;

const ListWrapper = styled.div`
	height: 400px;
`;

type AdoIdentity = {
	id: string;
	displayName: string;
	uniqueName: string;
};
type AdoUser = {
	identity: AdoIdentity;
};

type AdoProject = {
	account: string;
	id: string;
	name: string;
	url: string;
};

type AdoImportUsersModalProps = {
	closeModal: () => void;
	company: AdoImportUsersModal_company;
};

const AdoImportUsersModal = ({closeModal, company}: AdoImportUsersModalProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [adoUsers, setAdoUsers] = useState<AdoUser[]>([]);
	const [filteredAdoUsers, setFilteredAdoUsers] = useState<AdoUser[]>([]);
	const [selectedAdoUsers, setSelectedAdoUsers] = useState<string[]>([]);
	const [selectAll, setSelectAll] = useState<CheckedState>(false);
	const [importing, setImporting] = useState(false);
	// eslint-disable-next-line  @typescript-eslint/no-explicit-any
	const [profiles, setProfiles] = useState<any[]>([]);
	const [selectedProfile, setSelectedProfile] = useState<string | null>(null);
	const [projects, setProjects] = useState<AdoProject[]>([]);
	const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
	const disableImportButton = importing || selectedAdoUsers.length === 0 || selectedProfile === null;

	useEffect(() => {
		// Don't allow import of Admin users
		const profileEdges = (company.profiles?.edges ?? []) as ConnectionEdge<Profile>[];

		const filteredProfiles = getSelectableProfiles(profileEdges).filter(
			profile => !personHasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS, profile)
		);
		setProfiles(filteredProfiles);
		// Fetch ADO projects
		showLoader();
		DirectApi.Fetch(`vsts_projects`).then((adoProjects: AdoProject[]) => {
			hideLoader();
			setProjects(adoProjects);
		});
	}, []);

	useEffect(() => {
		if (selectedProjectId) {
			showLoader();
			const adoProject = projects.find(p => p.id === selectedProjectId);
			// Fetch Ado Users
			DirectApi.Fetch(`vsts_team_members?vsts_project_id=${selectedProjectId}&account=${adoProject?.account}`).then(
				response => {
					hideLoader();
					if (response.success) {
						setAdoUsers(response.userList);
						setFilteredAdoUsers(response.userList);
					} else {
						// TODO: Show error message
						console.log(response.error);
					}
				}
			);
		}
	}, [selectedProjectId]);

	const selectAllClick = () => {
		if (selectAll) {
			setSelectedAdoUsers([]);
			setSelectAll(false);
		} else {
			const selectedUsers = adoUsers.map(user => user.identity.id);
			setSelectedAdoUsers(selectedUsers);
			setSelectAll(true);
		}
	};

	const userSelect = (inputId: string) => {
		setSelectAll('indeterminate');
		if (selectedAdoUsers.includes(inputId)) {
			const selectedUsers = selectedAdoUsers.filter(id => id !== inputId);
			if (selectedUsers.length === 0) {
				setSelectAll(false);
			}
			setSelectedAdoUsers(selectedUsers);
		} else {
			const selectedUsers = [...selectedAdoUsers];
			selectedUsers.push(inputId);
			if (selectedUsers.length === adoUsers.length) {
				setSelectAll(true);
			}
			setSelectedAdoUsers(selectedUsers);
		}
	};

	const onFilterChange = (input: string) => {
		setFilteredAdoUsers(
			adoUsers.filter(user =>
				`${user.identity.displayName} ${user.identity.uniqueName}`.toLowerCase().includes(input.toLowerCase())
			)
		);
	};

	const onExportClick = () => {
		setImporting(true);
		showLoader();
		CommitMutation(
			CreateAdoUsersMutation,
			{
				users: adoUsers
					.filter(user => selectedAdoUsers.includes(user.identity.id))
					.map(
						adoUser =>
							({
								firstName: adoUser.identity.displayName,
								lastName: '',
								email: adoUser.identity.uniqueName,
								profileIds: [selectedProfile],
								active: false,
								invited: false,
								externalRefs: [
									{
										key: 'vsts_member_id',
										value: `${adoUser.identity.id}`,
									} as ExternalRef,
								],
							} as PersonInputType)
					),
			},
			response => {
				setImporting(false);
				closeModal();
				hideLoader();
				if (response.createAdoUsers?.errors?.length) {
					showModal({
						type: MODAL_TYPE.WARNING,
						message: 'The following Azure DevOps users could not be created:',
						warningInformation: response.createAdoUsers?.errors.map(error => error),
						buttons: [
							{
								text: formatMessage({id: 'common.ok'}),
								style: BUTTON_STYLE.FILLED,
								color: BUTTON_COLOR.WHITE,
							},
						],
					});
				} else {
					showModal({
						type: MODAL_TYPE.INTEGRATIONS_INFO,
						title: 'Import Azure DevOps Users',
						infoMessage: 'The resources have been created in Forecast as Deactivated team members.',
					});
				}
			},
			false
		);
	};

	return (
		<CreativeModal
			title={'Import Azure DevOps Users'}
			size={'l'}
			closeModal={closeModal}
			creativeButtonText={`Import Selected Azure DevOps Users (${selectedAdoUsers.length})`}
			cancelButtonText={formatMessage({id: 'common.cancel'})}
			onCreateHandler={onExportClick}
			onCancelHandler={closeModal}
			disabled={disableImportButton}
			replicateDesignSystem
		>
			<Text>
				{
					'Please choose the Azure DevOps resources that you would like to create in Forecast. The chosen resources will be created in Forecast as Deactivated team members.'
				}
			</Text>
			<ComboBox
				label="Permission Profile"
				defaultItems={profiles}
				value={selectedProfile}
				onChange={setSelectedProfile}
				width={ComboBox.WIDTH.FLEX}
			>
				{profile => (
					<ComboBox.Item key={profile.id} textValue={profile.name}>
						<Text>{profile.name}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>
			<ComboBox
				label="Azure Devops project"
				defaultItems={projects}
				value={selectedProjectId}
				onChange={setSelectedProjectId}
				width={ComboBox.WIDTH.FLEX}
			>
				{(project: AdoProject) => (
					<ComboBox.Item key={project.id} textValue={project.name}>
						<Text>{project.name}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>
			<FlexColumn gap={'xs'}>
				<Text color={Text.color.medium}>{`Search for Azure DevOps users`}</Text>
				<SearchField isExpandable={false} onChange={onFilterChange} width="600px" />
			</FlexColumn>
			<ListItem>
				<FlexRow gap={'xl'}>
					<Checkbox onChange={selectAllClick} checked={selectAll} />
					<SubHeading size={'2'}>{'Select all'}</SubHeading>
				</FlexRow>
			</ListItem>
			<ListWrapper>
				<Scrollbar maxHeight={'500px'}>
					<FlexColumn alignItems="flex-start" justifyContent="flex-start">
						{filteredAdoUsers &&
							filteredAdoUsers.map((user: AdoUser) => {
								return (
									<ListItem key={'adoUser-' + user.identity.id}>
										<FlexRow gap={'xl'}>
											<Checkbox
												onChange={() => userSelect(user.identity.id)}
												checked={selectedAdoUsers.includes(user.identity.id)}
											/>
											<Text
												size={'2'}
											>{`${user.identity.displayName} (${user.identity.uniqueName})`}</Text>
										</FlexRow>
									</ListItem>
								);
							})}
					</FlexColumn>
				</Scrollbar>
			</ListWrapper>
		</CreativeModal>
	);
};

const adoImportUsersModalQuery = graphql`
	query AdoImportUsersModal_Query {
		viewer {
			actualPersonId
			component(name: "ado_import_users_modal")
			company {
				...AdoImportUsersModal_company
			}
		}
	}
`;

export {adoImportUsersModalQuery};

export default createFragmentContainer(AdoImportUsersModal, {
	company: graphql`
		fragment AdoImportUsersModal_company on Company {
			profiles {
				edges {
					node {
						id
						name
						disabled
						permissions
					}
				}
			}
		}
	`,
});
